import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Button,
  Dialog,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect } from "react";

import {
  CARD_BORDER_RADIUS,
  DIALOG_RADIUS_SM,
  MOBILE_BREAKPOINT,
  PAGE_PADDING,
} from "@/constants/layout";
import { useAppSelector } from "@/store";

/**
 * The tenant's `announcement_message` — a back-office field for things like
 * "Eid hours" or "we're out of milkshakes today".
 *
 * The original shows it once per menu-page load, as a centred card the customer
 * has to acknowledge, and never on the landing page. It closes itself
 * immediately if the tenant has no message set, which is why the caller can
 * open it without checking first.
 */

const Panel = styled(Stack)(({ theme }) => ({
  maxWidth: 500,
  width: "95vw",
  maxHeight: "95vh",
  backgroundColor: theme.palette.background.default,
  borderRadius: CARD_BORDER_RADIUS,
  overflow: "hidden",
  padding: PAGE_PADDING,
}));

const Message = styled(Typography)({ whiteSpace: "pre-wrap" });

const Acknowledge = styled(Button)({
  borderRadius: DIALOG_RADIUS_SM,
  textTransform: "none",
  fontSize: "1rem",
  minWidth: 150,
});

interface AnnouncementDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function AnnouncementDialog({
  open,
  onClose,
}: AnnouncementDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
  const message = useAppSelector(
    (state) => state.settings.data.announcement_message,
  );

  useEffect(() => {
    if (open && !message) onClose();
    // Mirrors the original: the emptiness check runs on open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!message) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // MUI v5's Dialog exposes the surface as `PaperProps`; `slotProps.paper`
      // only arrives in v6, so it type-errors and is silently ignored here.
      PaperProps={{ sx: { backgroundColor: "transparent", boxShadow: "none" } }}
    >
      <Panel spacing={2} role="status">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
        >
          <CampaignIcon color="primary" fontSize="large" />
          <Typography component="h2" variant="h5">
            Announcement
          </Typography>
        </Stack>
        <Message textAlign="center" variant="body1">
          {message}
        </Message>
        <Stack direction="row" justifyContent="center" spacing={1}>
          <Acknowledge
            color="primary"
            variant="contained"
            onClick={onClose}
            endIcon={<CheckCircleOutlineIcon />}
            sx={isMobile ? { width: "100%" } : undefined}
          >
            Acknowledge
          </Acknowledge>
        </Stack>
      </Panel>
    </Dialog>
  );
}
